import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { login, selectAuthLoading, selectAuthError } from '../store/slices/authSlice';

const LoginContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const LogoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30%;
  background-color: #1A1625;
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
    padding-bottom: 30px;
  }
`;

const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 70%;
  padding: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

const LoginCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreatorText = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 500;
  margin-top: 8px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  align-self: flex-start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-self: center;
    margin-top: ${({ theme }) => theme.spacing.xl};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  align-self: flex-start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-self: center;
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  background-color: ${({ theme }) => theme.colors.background.main};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Button = styled.button`
  background-color: #9333EA; /* Using the purple color from the design */
  color: white;
  border: none;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 14px;
  }
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const SignupText = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.xxl};
    position: relative;
    bottom: 0;
  }
`;

const SignupLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const locationState = location.state as LocationState;
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Use the login action from authSlice which now accepts any email/password
      const result = await dispatch(login({ email, password })).unwrap();
      console.log('Login successful:', result);
      
      // Navigate to enterprises page
      navigate('/enterprises');
    } catch (err) {
      console.error('Login error:', err);
    }
  };
  
  return (
    <LoginContainer>
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
        </Logo>
      </LogoSide>
      <ContentSide>
        <LoginCard>
          <Title>Welcome Back</Title>
          <Subtitle>Enter your account details to login.</Subtitle>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email or Phone number</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email or phone number"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </FormGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <SignupText>
              Don't have an account? <SignupLink to="/signup">Sign Up</SignupLink>
            </SignupText>
          </Form>
        </LoginCard>
      </ContentSide>
    </LoginContainer>
  );
};

export default Login;
